/**
 * MCP connection URL helper.
 *
 * The MCP endpoint is served from the APEX domain (e.g. `sharpelab.xyz`) by
 * design, even though the dashboard SPA itself is served from a subdomain
 * (e.g. `app.sharpelab.xyz`). `app.sharpelab.xyz/mcp/...` does NOT work —
 * that host routes to the SPA, not the MCP server.
 *
 * This helper derives the MCP URL from the current browser origin, forcing
 * the apex host by stripping a leading `app.` label so the same code is
 * correct in both production and local dev without hardcoding a host:
 *   - https://app.sharpelab.xyz -> https://sharpelab.xyz/mcp/<token>
 *   - http://localhost:5173     -> http://localhost:5173/mcp/<token> (unchanged)
 *   - https://sharpelab.xyz     -> https://sharpelab.xyz/mcp/<token> (unchanged)
 */

/** Placeholder substituted when no token has been generated yet. */
const PLACEHOLDER_TOKEN = '<TOKEN>';

/**
 * Returns the current origin with any leading `app.` subdomain label
 * stripped, forcing the apex host.
 */
function apexOrigin(): string {
  const origin = window.location.origin;
  try {
    const url = new URL(origin);
    if (url.hostname.startsWith('app.')) {
      url.hostname = url.hostname.slice(4);
    }
    return url.origin;
  } catch {
    // `window.location.origin` should always be a valid URL, so this branch
    // should be unreachable in practice. Fall back to the raw origin rather
    // than throwing — this is a non-critical UX helper (worst case: the
    // rendered URL keeps the `app.` prefix and the user notices it's wrong).
    return origin;
  }
}

/**
 * Builds the MCP connection URL for the given token.
 *
 * `token` may be `null` when no token has been generated yet — in that case
 * the `<TOKEN>` placeholder is substituted. Callers that use a different
 * placeholder convention (e.g. `<your-token>`) should substitute it
 * themselves and pass the resulting string here — it is used verbatim.
 */
export function buildMcpUrl(token: string | null): string {
  const tok = token ?? PLACEHOLDER_TOKEN;
  return `${apexOrigin()}/mcp/${tok}`;
}
